import { Form, Modal, Select, Switch, Table } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUserRoles } from "../../../../../components/Forms/users/config";
import { AuthenticationService } from "../../../../../services";
import { ModalHeader } from "datastake-daf/dist/components";
import Footer from "../../../../../components/dashboards/components/project-visualisation/components/Footer";
import { MODAL_WIDTH } from "../../config";
import { useApplications } from "../../../../../context/Applications";
import Loading from "../../../../../components/Loading";

export default function UserTypeModal({
  userType = null,
  onClose = () => { },
  fetchUsers = () => { },
}) {
  const { t } = useTranslation();
  const { applications } = useApplications();
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const userRoles = useUserRoles();

  const [data, setData] = useState(
    (userType?.companyObject?.apps || []).filter((a) => a.access).map((app) => {
      const key = app.app;
      const c = applications.find((c) => c.app === key) || {};

      return {
        ...c,
        access: typeof userType.apps[key].access === 'boolean' ? userType.apps[key].access : true,
        role: userType.apps[key].role,
      };
    })
  );

  const changeAppData = useCallback((appKey, val) => {
    setHasChanges(true);

    setData((p) => p.map((app) => {
      if (app.app === appKey) {
        return { ...app, ...val };
      }

      return app;
    }));
  }, []);

  const columns = useMemo(() => [
    {
      title: t('Applications'),
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: t('Access'),
      dataIndex: 'access',
      key: 'access',
      ellipsis: true,
      render: (val, all) => {
        if (all.empty) {
          return <div className="daf-default-cell" />;
        }

        return (
          <div className="daf-default-cell">
            <Switch
              size="small"
              checked={val}
              onChange={(checked) => {
                changeAppData(all.app, { access: checked });
              }}
            />
          </div>
        )
      }
    },
    {
      title: t('Role'),
      dataIndex: 'role',
      key: 'role',
      ellipsis: true,
      render: (val, all) => {
        if (all.empty) {
          return <div className="daf-default-cell" />;
        }

        const roles = userRoles.filter((r) => {
          return r.app === all.app && r.canBeAssignedToSubUsers;
        });

        return (
          <div className="daf-default-cell">
            <Select
              value={val}
              size="small"
              disabled={!all.access}
              style={{ width: 150 }}
              onChange={(val) => {
                changeAppData(all.app, { role: val });
              }}
            >
              {roles.map((i) => (
                <Select.Option key={i.value} value={i.value}>
                  {i.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        );
      }
    },
  ], [t, changeAppData, userRoles]);

  const dataSource = useMemo(() => {
    const emptyDataSource = [];

    for (let i = 0; i < 10; i++) {
      emptyDataSource.push({ id: i, empty: true, key: i });
    }

    return emptyDataSource.map((empty, i) => data[i] || empty);
  }, [data]);

  const _onSubmit = async () => {
    setIsLoading(true);

    try {
      await AuthenticationService.editUserAccess(userType, data.map((d) => ({
        app: d.app,
        access: d.access,
        role: d.role,
      })));
      onClose();
      fetchUsers({});
    } catch (err) {
      console.log(err);
    }

    setIsLoading(false);
  };

  return (
    <Modal
      open={!!userType}
      onCancel={onClose}
      wrapClassName="bordered-footer"
      title={(
        <ModalHeader
          title={t('User type')}
        />
      )}
      footer={[
        <Footer
          t={t}
          key="footer"
          className="mt-0"
          onSubmit={_onSubmit}
          onClose={onClose}
          disabledFooter={!hasChanges || isLoading}
        />
      ]}
      width={MODAL_WIDTH}
    >
      <Form layout="vertical">
        <div className="daf-table-wrapper flex-1 flex-c extra-small small-body no-pagination">
          <Table
            renderWrapper={false}
            size="small"
            loading={isLoading}
            columns={columns}
            dataSource={dataSource}
            pagination={{ pageSize: 10 }}
            doEmptyRows
          />
        </div>
      </Form>
    </Modal>
  )
}
